import FalconCardHeader from 'components/common/FalconCardHeader';
import Flex from 'components/common/Flex';
import { useMemo } from 'react';
import { Card, Dropdown, DropdownDivider, Form } from 'react-bootstrap';
import { Link } from 'react-router-dom';

import AdvanceTable from 'components/common/advance-table/AdvanceTable';
import AdvanceTablePagination from 'components/common/advance-table/AdvanceTablePagination';
import AdvanceTableWrapper from 'components/common/advance-table/AdvanceTableWrapper';
import Avatar from 'components/common/Avatar';
import { AuthPost, AuthPut } from 'helpers/api/AuthCRUD';
import { API } from 'helpers/api/endpoints';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import 'react-loading-skeleton/dist/skeleton.css';
import { RootState } from 'store';
import { UserModel } from 'types';
import CreateUserModal from './CreateUserModal';
import ActionDropdown from 'components/common/ActionDropdown';

const ROLE_OPTIONS = ['customeradmin', 'user'];

type CustomerUsersDetaisTableProps = {
  users: UserModel[];
  refetch: () => void;
};
const CustomerUsersDetaisTable = ({
  users,
  refetch,
}: CustomerUsersDetaisTableProps) => {
  const { t } = useTranslation();
    const { token } = useSelector((state: RootState) => state);
  const tableColumns = useMemo(() => columns({ t, refetch ,token}), [t, refetch,token]);
  return (
    <AdvanceTableWrapper
      rowCount={users.length}
      columns={tableColumns}
      data={users}
      sortable
      pagination={true}
      perPage={8}
      selectionColumnWidth={10}
    >
      <Card className="mb-xl-3">
        <FalconCardHeader
          title={t('connected_user', { ns: 'home' })}
          titleTag="h6"
          endEl={<CreateUserModal onCreated={refetch} />}
        />

        <Card.Body>
          <AdvanceTable
            table
            rowClassName=" align-middle white-space-nowrap hover-actions-trigger btn-reveal-trigger hover-bg-100"
            tableProps={{
              className: 'fs--1 mb-0 overflow-hidden',
            }}
          />
        </Card.Body>
        <Card.Footer>
          <AdvanceTablePagination table />
        </Card.Footer>
      </Card>
    </AdvanceTableWrapper>
  );
};

type ColumnsProps = {
  t: ReturnType<typeof useTranslation>['t'];
  refetch: () => void;
  token:string | null;
};

const columns = ({ t, refetch, token }: ColumnsProps) => {

  return [
    {
      accessorKey: 'name',
      header: t('name', { ns: 'labels' }),
      meta: {
        headerProps: {
          className: 'py-3 ps-2',
          style: { minWidth: '12rem' },
        },
        cellProps: {
          className: 'ps-2',
        },
      },
      cell: ({ row }: any) => {
        return <UserItem user={row.original} />;
      },
    },
    {
      accessorKey: 'email',
      header: t('Email', { ns: 'labels' }),
      meta: {
        headerProps: {
          className: 'py-3',
        },
        cellProps: {
          className: 'text-700',
        },
      },
      cell: ({ row }: any) => {
        return <span className="text-700">{row.original.email ?? ''}</span>;
      },
    },
    {
      accessorKey: 'role',
      header: t('Role', { ns: 'benutzerliste' }),
      enableSorting: false,
      meta: {
        headerProps: {
          className: 'py-3 text-end',
        },
        cellProps: {
          className: 'text-end',
        },
      },
      cell: ({ row }: any) => {
        return <RoleSelect user={row.original} refetch={refetch} />;
      },
    },    {
      id: 'actions',
      header: t("Action"),
      enableSorting: false,
      meta:{
                headerProps: {
          className: 'py-3 text-end',
        },
      },
      cell: ({ row }) => {
        const user = row.original;
        const { isActive } = row.original;


        return (
          <div className='text-start'>
            <ActionDropdown btnRevealClass="btn-reveal-sm" drop="start">
              <div className="py-2">
                <Dropdown.Item>
                 <Link to={`/customeradmin/edituser/${user.guid}`} >
                  {t('edit_user', { ns: 'benutzerliste' })}
                </Link>
                </Dropdown.Item>

                  <DropdownDivider/>

                { !isActive ? (
                    <Dropdown.Item className="text-primary fw-semibold"
                      onClick={async () => {
                        await AuthPost(API.auth.resendInvitation(user.guid), {}, token!);
                        refetch();
                      }}
                    >
                      {t('resent_mail', { ns: 'benutzerliste' })}
                    </Dropdown.Item>
                ) : (
                  <Dropdown.Item
                    className="text-primary fw-semibold"
                    onClick={async () => {
                      await AuthPost(API.auth.resetPasswordMail(user.guid), {}, token!);
                      refetch();
                    }}
                  >
                    {t('reset_password_mail', { ns: 'benutzerliste' })}
                  </Dropdown.Item>
                )
              }
              </div>
            </ActionDropdown>
          </div>
        );
      }
    },
  ];
};

type UserItemProps = {
  user: UserModel;
};

const isNull = (arg: string | null | undefined) => {
  return arg ? arg : '';
};

const UserItem = ({ user }: UserItemProps) => {
  const { firstname, lastname, guid } = user;

  return (
    <Link to={`/customeradmin/edituser/${guid}`} className="text-800">
      <Flex alignItems="center">
        <Avatar size="xl" name={isNull(firstname) || undefined} />
        <h6 className="mb-0 ps-2">{`${isNull(firstname)} ${isNull(
          lastname
        )}`}</h6>
      </Flex>
    </Link>
  );
};

type RoleSelectProps = {
  user: UserModel;
  refetch: () => void;
};

const RoleSelect = ({ user, refetch }: RoleSelectProps) => {
  const { token } = useSelector((state: RootState) => state);

  // UserModel.role is typed as the Role enum, but the API returns the role
  // name as a string, so coerce to string for the <select> value.
  const currentRole = user.role as unknown as string;

  const updateRole = async (value: string) => {
    if (!token) return;
    await AuthPut(
      API.users.updateUserRole(user.guid),
      { role: value },
      token
    );
    refetch();
  };

  // A customer admin may only assign customeradmin/user. If a user already has
  // a role outside that set (e.g. a global admin), show it as a disabled option
  // so their real role is still displayed instead of being silently reassigned.
  const showCurrentAsDisabled =
    !!currentRole && !ROLE_OPTIONS.includes(currentRole);

  return (
    <Form.Select
      size="sm"
      className="w-auto ms-auto"
      value={currentRole ?? ''}
      onChange={(e) => updateRole(e.target.value)}
    >
      {showCurrentAsDisabled && (
        <option value={currentRole} disabled>
          {currentRole}
        </option>
      )}
      {ROLE_OPTIONS.map((item) => (
        <option key={item} value={item}>
          {item}
        </option>
      ))}
    </Form.Select>
  );
};

export default CustomerUsersDetaisTable;
